import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { RED_SCROLLBAR_SX } from './theme/tokens.js';
import { FILTER_FIELD_SX, FILTER_LABEL_SX } from './theme/filterBarStyles.js';

/**
 * Shared multi-select checkbox dropdown with Select all / Clear, an optional
 * in-list search box, and an inline selected-count chip on the label.
 *
 * This is a straight port of the WORKING `MultiSelectFilter` in the app's
 * `EffortsFilterBar.jsx`. The Efforts copy is intentionally left untouched so
 * Efforts behaviour does not change; this shared version is what other pages
 * opt into for the specific filters they convert.
 *
 * Differences from the Efforts copy, all additive / opt-in so the look and
 * behaviour match whatever the host page's single-select showed before:
 *   - `renderOption(option, { selected })` lets a page draw a rich option row
 *     (e.g. ui-kit's `RichFilterOption`) in place of the plain label.
 *   - `getOptionValue` separates an option's stored VALUE (used for selection,
 *     wire + URL) from its visible label, matching the host `FilterSelect`'s
 *     `getOptionValue` / `getOptionLabel` split. `options` may therefore be
 *     objects, not just strings.
 *   - `sx` / `placeholder` / `disabled` are forwarded so the closed trigger
 *     keeps the host page's styling, empty-state text and locked state.
 *
 * Selection is always an ARRAY of option VALUES. Toggling never reorders the
 * caller's array (append-on-select, filter-on-deselect); any wire/URL ordering
 * (e.g. sorting for a stable cache key) is the caller's/API layer's concern.
 *
 * @param {Object} props
 * @param {string} props.label - Uppercase caption shown above the control.
 * @param {Array<string|object>} props.value - Selected option VALUES.
 * @param {Array<string|object>} props.options - Option list (strings or objects).
 * @param {(next: Array) => void} props.onChange - Receives the next value array.
 * @param {() => void} [props.onClose] - Called when the menu closes.
 * @param {boolean} [props.loading=false] - Show a spinner + "Loading…" row.
 * @param {boolean} [props.disabled=false] - Lock the control.
 * @param {string} [props.placeholder='All'] - Empty-selection trigger text.
 * @param {(o: any) => string|number} [props.getOptionValue] - Option -> value.
 * @param {(o: any) => string} [props.getOptionLabel] - Option -> visible label.
 * @param {(o: any, state: { selected: boolean }) => React.ReactNode} [props.renderOption]
 *   - Custom option-row renderer (rich rows). Falls back to the label.
 * @param {object} [props.sx] - sx applied to the closed Select trigger.
 * @param {number} [props.valueFontWeight=600] - Font weight of the selected
 *   value text in the closed trigger. Opt into a lighter weight (e.g. 500) so
 *   the value matches sibling single-select filters on the same bar.
 */

/** Show the in-list search box once a list has more than this many options. */
const SEARCH_THRESHOLD = 8;

/** Reference palette copied from the Efforts MultiSelectFilter (SalesPulse). */
const SD = {
  activeText: '#0F172A',
  activeBorder: '#1D4ED8',
  activeFill: '#EFF6FF',
  idleText: '#94A3B8',
  idleBorder: '#E2E8F0',
  itemTitle: '#0F172A',
  itemDivider: '#F8FAFC',
  selectedBg: '#EFF6FF',
  selectedText: '#1D4ED8',
  metaText: '#64748B',
  rowHover: '#F8FAFC',
  scrollThumb: '#CBD5E1',
  chipBg: '#F1F5F9',
  chipBorder: '#E2E8F0',
  chipText: '#94A3B8',
  searchBorder: '#E2E8F0',
  searchFocus: '#1D4ED8',
  searchBg: '#F8FAFC',
};

/** Dropdown panel: white, 10px radius, soft shadow, app-wide red scrollbar. */
const MENU_PAPER_SX = {
  maxHeight: 320,
  borderRadius: '10px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
  ...RED_SCROLLBAR_SX,
};

/** Closed-select style: light-blue fill + blue border when a value is chosen.
 * `valueFontWeight` is the selected-value text weight (default 600); pages can
 * opt into a lighter weight so the value text matches sibling single-selects. */
const triggerSx = (hasSelection, valueFontWeight = 600) => ({
  borderRadius: '8px',
  bgcolor: hasSelection ? SD.activeFill : 'background.paper',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasSelection ? SD.activeBorder : SD.idleBorder,
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: hasSelection ? SD.activeBorder : '#94A3B8',
  },
  '& .MuiSelect-select': {
    fontWeight: valueFontWeight,
    color: hasSelection ? SD.activeText : SD.idleText,
    fontSize: '13px',
  },
});

// Shared field wrapper (one source of truth across all filter bars): a sensible
// min width on flex bars, but allowed to shrink to fit a CSS-grid cell on
// narrow screens so nothing is clipped.
const FIELD_SX = FILTER_FIELD_SX;

/** Inline selected-count chip rendered right after a filter label. */
function CountChip({ count }) {
  if (!count) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 17,
        height: 15,
        px: '4px',
        borderRadius: '8px',
        bgcolor: SD.chipBg,
        border: '1px solid',
        borderColor: SD.chipBorder,
        color: SD.chipText,
        fontSize: '9px',
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  );
}

export function MultiSelectFilter({
  label,
  value = [],
  options = [],
  onChange,
  onClose,
  loading = false,
  disabled = false,
  placeholder = 'All',
  getOptionValue = (o) => o,
  getOptionLabel = (o) => o,
  renderOption = null,
  sx = null,
  valueFontWeight = 600,
}) {
  const [search, setSearch] = useState('');

  // Index options by value for cheap value -> option / value -> label lookups.
  const optionByValue = useMemo(() => {
    const map = new Map();
    (Array.isArray(options) ? options : []).forEach((o) => {
      map.set(String(getOptionValue(o)), o);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const labelForValue = (v) => {
    const opt = optionByValue.get(String(v));
    return opt != null ? getOptionLabel(opt) : String(v);
  };

  // Preserve any selected values whose option hasn't loaded yet, so a selection
  // survives before options arrive and stays toggleable.
  const allOptions = useMemo(() => {
    const present = new Set((Array.isArray(options) ? options : []).map((o) => String(getOptionValue(o))));
    const extras = (Array.isArray(value) ? value : [])
      .filter((v) => !present.has(String(v)));
    return [...extras, ...(Array.isArray(options) ? options : [])];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  const showSearch = allOptions.length > SEARCH_THRESHOLD;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => String(getOptionLabel(o) ?? '').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOptions, search]);

  const selectedSet = useMemo(
    () => new Set((Array.isArray(value) ? value : []).map((v) => String(v))),
    [value],
  );

  const toggle = (optionValue) => {
    const key = String(optionValue);
    if (selectedSet.has(key)) {
      onChange(value.filter((v) => String(v) !== key));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const selectAll = () => {
    const next = new Set(value.map((v) => String(v)));
    const merged = [...value];
    filtered.forEach((o) => {
      const ov = getOptionValue(o);
      if (!next.has(String(ov))) {
        next.add(String(ov));
        merged.push(ov);
      }
    });
    onChange(merged);
  };
  const clear = () => onChange([]);

  return (
    <Box sx={FIELD_SX}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        <Typography component="label" sx={FILTER_LABEL_SX}>
          {label}
        </Typography>
        <CountChip count={value.length} />
      </Box>
      <FormControl size="small" fullWidth disabled={disabled || loading}>
        <Select
          multiple
          displayEmpty
          value={value}
          onOpen={() => setSearch('')}
          onClose={() => onClose?.()}
          onChange={() => {}}
          sx={{ ...triggerSx(value.length > 0, valueFontWeight), ...(sx || {}) }}
          renderValue={(selected) =>
            selected.length === 0 ? (
              <Box component="span" sx={{ color: SD.idleText }}>
                {placeholder}
              </Box>
            ) : selected.length === 1 ? (
              labelForValue(selected[0])
            ) : (
              `${selected.length} selected`
            )
          }
          endAdornment={
            loading ? (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <CircularProgress size={16} aria-label={`Loading ${label} options`} />
              </InputAdornment>
            ) : null
          }
          MenuProps={{ PaperProps: { sx: MENU_PAPER_SX }, autoFocus: false }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5 }}>
            <Button
              size="small"
              onClick={selectAll}
              disabled={filtered.length === 0}
              sx={{ fontWeight: 600, color: SD.selectedText }}
            >
              Select all
            </Button>
            <Button
              size="small"
              onClick={clear}
              disabled={value.length === 0}
              sx={{ fontWeight: 600, color: SD.metaText }}
            >
              Clear
            </Button>
          </Box>

          {showSearch ? (
            <ListSubheader sx={{ p: 1, bgcolor: 'background.paper' }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder={`Search ${String(label).toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: SD.searchBg },
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: SD.searchBorder,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': { bgcolor: '#fff' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: SD.searchFocus,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </ListSubheader>
          ) : null}

          <Divider />

          {loading && allOptions.length === 0 ? (
            <MenuItem disabled>
              <em>Loading…</em>
            </MenuItem>
          ) : null}

          {!loading && filtered.length === 0 ? (
            <MenuItem disabled>
              <em>No matches</em>
            </MenuItem>
          ) : null}

          {filtered.map((option) => {
            const ov = getOptionValue(option);
            const isSelected = selectedSet.has(String(ov));
            return (
              <MenuItem
                key={String(ov)}
                value={ov}
                onClick={() => toggle(ov)}
                dense
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: SD.itemDivider,
                  bgcolor: isSelected ? SD.selectedBg : 'transparent',
                  whiteSpace: renderOption ? 'normal' : undefined,
                  '&:hover': { bgcolor: isSelected ? SD.selectedBg : SD.rowHover },
                }}
              >
                <Checkbox checked={isSelected} size="small" sx={{ py: 0, mr: 0.5, mt: '1px' }} />
                {renderOption ? (
                  renderOption(option, { selected: isSelected })
                ) : (
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 600,
                      fontSize: '12.5px',
                      color: isSelected ? SD.selectedText : SD.itemTitle,
                    }}
                  >
                    {getOptionLabel(option)}
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}

export default MultiSelectFilter;
